import { Card, Input, Typography, Button, Flex, Segmented, Tag, Space, Popover, Radio, Select, Divider, Grid, Tooltip } from 'antd';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PlusOutlined, AppstoreOutlined, BarsOutlined, FilterOutlined, SettingOutlined, SortAscendingOutlined } from '@ant-design/icons';
import { filterBySearch } from '../../utils/search';
import { highlightMatch } from '../../utils/highlight';
import ExcelUploadModal from '../../components/ExcelUpload';
import certificateStore from '../../store/CertificateStore';
import { observer } from 'mobx-react-lite';
import Loader from '../../components/Loader';
import CertificateModalForm from '../../components/Certificates/CertificateModalForm';
import employeeStore from '../../store/EmployeeStore';
import tagStore from '../../store/TagStore';
import TagManagementModal from '../../components/Tags/TagManagementModal';
import dayjs from 'dayjs';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

// Single status tag for certificate cards
const getStatusTag = (expired, hasDeadline) => {
    if (!hasDeadline) return <Tag color="blue">Бессрочный</Tag>;

    const now = dayjs();
    const expDate = dayjs(expired);
    const diffDays = expDate.diff(now, 'day');

    if (diffDays < 0) {
        return <Tag color="red">Просрочен ({Math.abs(diffDays)} д.)</Tag>;
    } else if (diffDays === 0) {
        return <Tag color="red">Истекает сегодня</Tag>;
    } else if (diffDays <= 7) {
        return <Tag color="orange">Осталось {diffDays} д.</Tag>;
    } else if (diffDays <= 30) {
        return <Tag color="gold">Осталось {diffDays} д.</Tag>;
    } else {
        return <Tag color="green">{expDate.format('DD.MM.YYYY')}</Tag>;
    }
};

// Calculate how many tags fit in one row based on total character count
// Calculate how many tags fit based on weight system
// Tag weight = 6 + character count; "+N" badge weight = 6
// Max weight depends on card width; 2 rows allowed when width > 200
const getVisibleTags = (tags, cardWidth) => {
    if (!tags || tags.length === 0 || cardWidth <= 0) return { visible: [], hidden: 0 };
    
    // Determine max weight per row based on card width
    let rowWeight;
    if (cardWidth < 160) rowWeight = 10;
    else if (cardWidth < 200) rowWeight = 22;
    else if (cardWidth < 240) rowWeight = 28;
    else if (cardWidth < 280) rowWeight = 35;
    else rowWeight = 40;
    
    const maxRows = cardWidth > 270 ? 2 : 1;
    const maxWeight = rowWeight * maxRows;
    
    const visible = [];
    let totalWeight = 0;
    
    for (const tag of tags) {
        const tagWeight = 6 + tag.name.length;
        const remaining = tags.length - visible.length - 1;
        const needsBadge = remaining > 0;
        const futureTotal = totalWeight + tagWeight + (needsBadge ? 6 : 0);
        
        if (futureTotal > maxWeight) break;
        totalWeight += tagWeight;
        visible.push(tag);
    }
    
    const hidden = tags.length - visible.length;
    return { visible, hidden };
};

// Dynamic ellipsis rows based on card width and tag presence
const getEllipsisRows = (cardWidth, hasTags) => {
    const twoRowTags = cardWidth > 200 && hasTags;
    if (cardWidth < 140) return hasTags ? 2 : 2;
    if (cardWidth < 190) return hasTags ? 3 : 4;
    if (cardWidth < 240) return twoRowTags ? 3 : (hasTags ? 4 : 5);
    return twoRowTags ? 4 : (hasTags ? 5 : 6);
};


const Certificates = observer(() => {
    const { loading: employeesLoading } = employeeStore;
    const { certificates, loading: certificatesLoading, fetchCertificates } = certificateStore;

    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    
    useEffect(() => {
        employeeStore.fetchEmployees();
        certificateStore.fetchCertificates();
        tagStore.fetchTags();
    }, []);
    
    // Initialize from URL params
    const [searchText, setSearchText] = useState(searchParams.get('search') || '');
    const [open, setOpen] = useState(false);
    const [editingCert, setEditingCert] = useState(null);
    const [viewMode, setViewMode] = useState(searchParams.get('view') || 'list'); // 'list' or 'grid'
    
    // Filter states
    const [sortOrder, setSortOrder] = useState(searchParams.get('sort') || 'asc'); // 'asc' or 'desc'
    const [sortField, setSortField] = useState(searchParams.get('sortField') || 'date'); // 'date' or 'employee'
    const [selectedEmployees, setSelectedEmployees] = useState(
        searchParams.get('employees') ? searchParams.get('employees').split(',').map(Number) : []
    ); // employee IDs
    const [selectedTags, setSelectedTags] = useState(
        searchParams.get('tags') ? searchParams.get('tags').split(',').map(Number) : []
    ); // tag IDs
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all'); // 'all', 'expired', 'active'
    const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
    const [sortPopoverOpen, setSortPopoverOpen] = useState(false);
    const [tagManagementOpen, setTagManagementOpen] = useState(false);
    
    // Card width for character-based tag calculation
    const [cardWidth, setCardWidth] = useState(200);
    const gridRef = useRef(null);
    const popoverClosedAt = useRef(0);
    const filterContentRef = useRef(null);

    useEffect(() => {
        if (viewMode !== 'grid' || !gridRef.current) return;
        
        const updateCardWidth = () => {
            const firstCard = gridRef.current?.querySelector('.ant-card');
            if (!firstCard) return;
            setCardWidth(firstCard.offsetWidth);
        };
        
        updateCardWidth();
        const observer = new ResizeObserver(updateCardWidth);
        observer.observe(gridRef.current);
        return () => observer.disconnect();
    }, [viewMode]);

    // Update URL when filters change
    useEffect(() => {
        const params = {};
        if (searchText) params.search = searchText;
        if (viewMode !== 'list') params.view = viewMode;
        if (sortOrder !== 'asc') params.sort = sortOrder;
        if (sortField !== 'date') params.sortField = sortField;
        if (selectedEmployees.length > 0) params.employees = selectedEmployees.join(',');
        if (selectedTags.length > 0) params.tags = selectedTags.join(',');
        if (statusFilter !== 'all') params.status = statusFilter;
        setSearchParams(params, { replace: true });
    }, [searchText, viewMode, sortOrder, sortField, selectedEmployees, selectedTags, statusFilter, setSearchParams]);

    // Filter by search
    const filtered = filterBySearch(certificates, searchText, (item) => {
        const employeeName = employeeStore.getEmployeeById(item.employeeId)?.name || '';
        const tagNames = tagStore.getTagsByIds(item.tagIds || []).map(t => t.name).join(' ');
        return [
            item.name,
            employeeName,
            item.info,
            tagNames,
        ];
    });

    // Apply filters and sort
    const sorted = useMemo(() => {
        let result = [...filtered];
        
        // Filter by employees
        if (selectedEmployees.length > 0) {
            result = result.filter(cert => selectedEmployees.includes(cert.employeeId));
        }
        
        // Filter by tags
        if (selectedTags.length > 0) {
            result = result.filter(cert => {
                const certTagIds = cert.tagIds || [];
                return selectedTags.some(tagId => certTagIds.includes(tagId));
            });
        }
        
        // Filter by status
        if (statusFilter === 'expired') {
            result = result.filter(cert => {
                if (!cert.hasDeadline) return false;
                return dayjs(cert.expired).isBefore(dayjs(), 'day');
            });
        } else if (statusFilter === 'active') {
            result = result.filter(cert => {
                if (!cert.hasDeadline) return true;
                return !dayjs(cert.expired).isBefore(dayjs(), 'day');
            });
        }
        
        // Sort
        result.sort((a, b) => {
            if (sortField === 'employee') {
                const nameA = (employeeStore.getEmployeeById(a.employeeId)?.name || '').toLowerCase();
                const nameB = (employeeStore.getEmployeeById(b.employeeId)?.name || '').toLowerCase();
                const cmp = nameA.localeCompare(nameB, 'ru');
                return sortOrder === 'asc' ? cmp : -cmp;
            }
            // Sort by expiration date
            if (!a.hasDeadline && b.hasDeadline) return 1;
            if (a.hasDeadline && !b.hasDeadline) return -1;
            if (!a.hasDeadline && !b.hasDeadline) return 0;
            const dateComparison = new Date(a.expired) - new Date(b.expired);
            return sortOrder === 'asc' ? dateComparison : -dateComparison;
        });
        
        return result;
    }, [filtered, selectedEmployees, selectedTags, statusFilter, sortOrder, sortField]);

    if (certificatesLoading && employeesLoading) return <Loader/>;

    return (
        <>
            <Flex style={{ marginBottom: 8 }} wrap gap="small">
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setEditingCert(null);
                        setOpen(true);
                    }}
                >
                    Добавить удостоверение
                </Button>

                <CertificateModalForm
                    open={open}
                    onClose={() => setOpen(false)}
                    certificate={editingCert}
                />

                <ExcelUploadModal/>
            </Flex>

            <Flex style={{ marginBottom: 8 }}>
                <Input.Search
                    placeholder="Поиск…"
                    allowClear
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: '100%' }}
                />
            </Flex>

            <Flex style={{ marginBottom: 16 }} wrap gap="small">

                <Popover
                    open={filterPopoverOpen}
                    onOpenChange={(open) => {
                        if (!open) popoverClosedAt.current = Date.now();
                        setFilterPopoverOpen(open);
                    }}
                    trigger="click"
                    placement="bottomRight"
                    content={
                        <div style={{ width: 300 }} ref={filterContentRef}>
                            <div style={{ marginBottom: 16 }}>
                                <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
                                    <Text strong>Сотрудники:</Text>
                                    <Button type="link" size="small" style={{ padding: 0, height: 'auto', visibility: selectedEmployees.length > 0 ? 'visible' : 'hidden' }} onClick={() => setSelectedEmployees([])}>
                                        Очистить
                                    </Button>
                                </Flex>
                                <Select
                                    mode="multiple"
                                    value={selectedEmployees}
                                    onChange={setSelectedEmployees}
                                    placeholder="Все сотрудники"
                                    style={{ width: '100%' }}
                                    showSearch
                                    optionFilterProp="children"
                                    getPopupContainer={() => filterContentRef.current || document.body}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={employeeStore.employees.map(emp => ({
                                        value: emp.id,
                                        label: emp.name
                                    }))}
                                    maxTagCount="responsive"
                                />
                            </div>

                            <Divider style={{ margin: '12px 0' }} />

                            <div style={{ marginBottom: 16 }}>
                                <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
                                    <Text strong>Теги:</Text>
                                    <Button type="link" size="small" style={{ padding: 0, height: 'auto', visibility: selectedTags.length > 0 ? 'visible' : 'hidden' }} onClick={() => setSelectedTags([])}>
                                        Очистить
                                    </Button>
                                </Flex>
                                <Select
                                    mode="multiple"
                                    value={selectedTags}
                                    onChange={setSelectedTags}
                                    placeholder="Все теги"
                                    style={{ width: '100%' }}
                                    showSearch
                                    getPopupContainer={() => filterContentRef.current || document.body}
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    optionRender={(option) => (
                                        <Tag color={option.data?.color} style={{ marginRight: 0 }}>
                                            {option.label}
                                        </Tag>
                                    )}
                                    options={tagStore.tags.map(tag => ({
                                        value: tag.id,
                                        label: tag.name,
                                        color: tag.color,
                                    }))}
                                    maxTagCount="responsive"
                                    tagRender={(props) => {
                                        const { label, value, closable, onClose } = props;
                                        const tag = tagStore.getTagById(value);
                                        return (
                                            <Tag
                                                color={tag?.color}
                                                closable={closable}
                                                onClose={onClose}
                                                style={{ marginRight: 3 }}
                                            >
                                                {label}
                                            </Tag>
                                        );
                                    }}
                                    dropdownRender={(menu) => (
                                        <>
                                            {menu}
                                            <Divider style={{ margin: '8px 0' }} />
                                            <Button
                                                type="link"
                                                size="small"
                                                onClick={() => {
                                                    setFilterPopoverOpen(false);
                                                    setTimeout(() => setTagManagementOpen(true), 100);
                                                }}
                                                style={{ padding: '4px 12px 8px 12px', width: '100%', textAlign: 'left' }}
                                                icon={<SettingOutlined />}
                                            >
                                                Настроить теги
                                            </Button>
                                        </>
                                    )}
                                />
                            </div>

                            <Divider style={{ margin: '12px 0' }} />

                            <div>
                                <Text strong style={{ display: 'block', marginBottom: 8 }}>Статус:</Text>
                                <Radio.Group 
                                    value={statusFilter} 
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    style={{ width: '100%' }}
                                >
                                    <Radio value="all" style={{ display: 'block', marginBottom: 4 }}>
                                        Все
                                    </Radio>
                                    <Radio value="active" style={{ display: 'block', marginBottom: 4 }}>
                                        Действующие
                                    </Radio>
                                    <Radio value="expired" style={{ display: 'block' }}>
                                        Просроченные
                                    </Radio>
                                </Radio.Group>
                            </div>

                            <Divider style={{ margin: '12px 0' }} />

                            <Flex gap="small">
                                {(selectedEmployees.length > 0 || selectedTags.length > 0 || statusFilter !== 'all') && (
                                    <Button 
                                        block
                                        onClick={() => {
                                            setSelectedEmployees([]);
                                            setSelectedTags([]);
                                            setStatusFilter('all');
                                        }}
                                    >
                                        Сбросить
                                    </Button>
                                )}
                                <Button 
                                    block
                                    type="primary"
                                    onClick={() => setFilterPopoverOpen(false)}
                                >
                                    Готово
                                </Button>
                            </Flex>
                        </div>
                    }
                >
                    <Button 
                        icon={<FilterOutlined />} 
                        type={selectedEmployees.length > 0 || selectedTags.length > 0 || statusFilter !== 'all' ? 'primary' : 'default'}
                    />
                </Popover>

                <Popover
                    open={sortPopoverOpen}
                    onOpenChange={(open) => {
                        if (!open) popoverClosedAt.current = Date.now();
                        setSortPopoverOpen(open);
                    }}
                    trigger="click"
                    placement="bottomLeft"
                    content={
                        <div style={{ width: 260 }}>
                            <div>
                                <Text strong style={{ display: 'block', marginBottom: 8 }}>Сортировать по:</Text>
                                <Radio.Group 
                                    value={sortField} 
                                    onChange={(e) => setSortField(e.target.value)}
                                    style={{ width: '100%' }}
                                >
                                    <Radio value="date" style={{ display: 'block', marginBottom: 4 }}>
                                        Дата истечения
                                    </Radio>
                                    <Radio value="employee" style={{ display: 'block' }}>
                                        Сотрудник (А-Я)
                                    </Radio>
                                </Radio.Group>
                            </div>

                            <Divider style={{ margin: '12px 0' }} />

                            <div>
                                <Text strong style={{ display: 'block', marginBottom: 8 }}>Порядок:</Text>
                                <Radio.Group 
                                    value={sortOrder} 
                                    onChange={(e) => setSortOrder(e.target.value)}
                                    style={{ width: '100%' }}
                                >
                                    <Radio value="asc" style={{ display: 'block', marginBottom: 4 }}>
                                        {sortField === 'date' ? 'Скоро истекают' : 'А → Я'}
                                    </Radio>
                                    <Radio value="desc" style={{ display: 'block' }}>
                                        {sortField === 'date' ? 'Длительность действия' : 'Я → А'}
                                    </Radio>
                                </Radio.Group>
                            </div>

                            <Divider style={{ margin: '12px 0' }} />

                            <Flex gap="small">
                                {(sortOrder !== 'asc' || sortField !== 'date') && (
                                    <Button 
                                        block
                                        onClick={() => {
                                            setSortOrder('asc');
                                            setSortField('date');
                                        }}
                                    >
                                        Сбросить
                                    </Button>
                                )}
                                <Button 
                                    block
                                    type="primary"
                                    onClick={() => setSortPopoverOpen(false)}
                                >
                                    Готово
                                </Button>
                            </Flex>
                        </div>
                    }
                >
                    <Button 
                        icon={<SortAscendingOutlined />} 
                        type={sortOrder !== 'asc' || sortField !== 'date' ? 'primary' : 'default'}
                    />
                </Popover>

                <Segmented
                    value={viewMode}
                    onChange={setViewMode}
                    options={[
                        { value: 'list', icon: <BarsOutlined /> },
                        { value: 'grid', icon: <AppstoreOutlined /> },
                    ]}
                />

                <TagManagementModal
                    open={tagManagementOpen}
                    onClose={() => setTagManagementOpen(false)}
                />

            </Flex>

            <div style={{ 
                maxHeight: '70vh', 
                overflowY: 'auto',
                margin: '-8px',
                padding: '8px',
            }}>
                <div ref={viewMode === 'grid' ? gridRef : undefined} style={viewMode === 'grid' ? {
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: '12px',
                    maxWidth: '100%',
                } : {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                }}>
                    {sorted.map((cert) => {
                        const employeeName = employeeStore.getEmployeeById(cert.employeeId)?.name || 'Не указан';
                        const tagCount = cert.tagIds?.length || 0;
                        
                        return (
                                <Card
                                    key={cert.id}
                                    hoverable
                                    onMouseDown={() => {
                                        if (filterPopoverOpen || sortPopoverOpen) {
                                            popoverClosedAt.current = Date.now();
                                        }
                                    }}
                                    onClick={() => {
                                        if (Date.now() - popoverClosedAt.current < 300) return;
                                        navigate(`/certificates/${cert.id}`);
                                    }}
                                    style={viewMode === 'grid' ? {
                                        aspectRatio: '1',
                                        overflow: 'hidden',
                                    } : undefined}
                                    bodyStyle={viewMode === 'grid' ? {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        flex: 1,
                                        padding: '12px',
                                        overflow: 'hidden',
                                        height: '100%',
                                    } : undefined}
                                >
                                    {viewMode === 'list' ? (
                                        // List mode: parameters on first line, title on second
                                        <Flex vertical gap="small">
                                            <Space size={8} wrap>
                                                {getStatusTag(cert.expired, cert.hasDeadline)}
                                                
                                                <Text style={{ fontSize: 14 }}>
                                                    {highlightMatch(employeeName, searchText, (t) => <Text mark>{t}</Text>)}
                                                </Text>
                                            </Space>
                                            
                                            <Title 
                                                level={5} 
                                                style={{ 
                                                    marginTop: 0, 
                                                    marginBottom: 0
                                                }}
                                            >
                                                {highlightMatch(cert.name, searchText, (t) => <Text mark>{t}</Text>)}
                                            </Title>

                                            {cert.tagIds && cert.tagIds.length > 0 && (
                                                <Space size={[4, 4]} wrap>
                                                    {tagStore.getTagsByIds(cert.tagIds).map(tag => (
                                                        <Tag 
                                                            key={tag.id} 
                                                            color={tag.color}
                                                            style={{ margin: 0 }}
                                                        >
                                                            {tag.name}
                                                        </Tag>
                                                    ))}
                                                </Space>
                                            )}
                                        </Flex>
                                    ) : (
                                        // Grid mode: vertical layout
                                        <>
                                            <div style={{ marginBottom: 8 }}>
                                                {getStatusTag(cert.expired, cert.hasDeadline)}
                                            </div>
                                            
                                            <div style={{ marginBottom: 8 }}>
                                                <Text style={{ fontSize: 14 }}>
                                                    {highlightMatch(employeeName, searchText, (t) => <Text mark>{t}</Text>)}
                                                </Text>
                                            </div>
                                            
                                            <div style={{ flex: 1, overflow: 'hidden' }}>
                                                <Title 
                                                    level={5} 
                                                    ellipsis={{ rows: getEllipsisRows(cardWidth, tagCount > 0), tooltip: { title: cert.name, getPopupContainer: () => document.body } }}
                                                    style={{ 
                                                        marginTop: 0, 
                                                        marginBottom: 0,
                                                        lineHeight: '1.4'
                                                    }}
                                                >
                                                    {highlightMatch(cert.name, searchText, (t) => <Text mark>{t}</Text>)}
                                                </Title>
                                            </div>

                                            {tagCount > 0 && (
                                                (() => {
                                                    const tags = tagStore.getTagsByIds(cert.tagIds);
                                                    const { visible: visibleTags, hidden: hiddenCount } = getVisibleTags(tags, cardWidth);
                                                    if (visibleTags.length === 0) return null;

                                                    return (
                                                        <div style={{ marginTop: 8, display: 'flex', flexWrap: cardWidth > 200 ? 'wrap' : 'nowrap', gap: 4, overflow: 'hidden', maxHeight: cardWidth > 200 ? 52 : 26 }}>
                                                            {visibleTags.map(tag => (
                                                                <Tag 
                                                                    key={tag.id} 
                                                                    color={tag.color}
                                                                    style={{ margin: 0, fontSize: 12, flexShrink: 0 }}
                                                                >
                                                                    {tag.name}
                                                                </Tag>
                                                            ))}
                                                            {hiddenCount > 0 && (
                                                                <Tag style={{ margin: 0, fontSize: 12, flexShrink: 0 }}>
                                                                    +{hiddenCount}
                                                                </Tag>
                                                            )}
                                                        </div>
                                                    );
                                                })()
                                            )}
                                        </>
                                    )}
                                </Card>
                        );
                    })}
                </div>
                
                {sorted.length === 0 && (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Text type="secondary">
                            {searchText ? 'Ничего не найдено' : 'Нет сертификатов'}
                        </Text>
                    </div>
                )}
            </div>
        </>
    );
})

export default Certificates;
